/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
  ConfigureListFilterResponse,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all device models from the API
 */
export const fetchAllModels = createAsyncThunk(
  'model/fetchAllModels',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchModels(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch models',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Edit an existing device model via PATCH
 */
export const editModel = createAsyncThunk(
  'model/editModel',
  async (
    {
      id,
      data,
      isFileUpload = false,
    }: { id: string; data: any; isFileUpload?: boolean },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await deviceHubApi.updateModel(id, data, isFileUpload);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to update model',
          status: true,
        },
      });
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Fetch model filter options
 */
export const fetchModelFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'model/fetchModelFilters',
  async (query, { dispatch }) => {
    try {
      const response = await deviceHubApi.modelFilter(query);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response?.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Model slice
 */
const initialState = {
  models: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 20,
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: null,
  sortField: null,
};

const modelSlice = createSlice({
  name: 'model',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setModelPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setModelLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateModelFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state - The state
     */
    disableModelFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort by
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setModelSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    /**
     * Set sort field
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setModelSortField: (state, action) => {
      state.sortField = action.payload;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllModels.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllModels.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchModelFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchModelFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchModelFilters.rejected, (state) => {
        state.filterListLoading = false;
      })
      .addCase(editModel.fulfilled, (state, action) => {
        const updatedModel =
          action.payload?.results?.[0] || action.payload?.data || action.payload;
        if (updatedModel && (updatedModel._id || updatedModel.id)) {
          const targetId = updatedModel._id || updatedModel.id;
          state.models = state.models.map((model: any) =>
            (model._id || model.id) === targetId ? { ...model, ...updatedModel } : model,
          );
        }
      });
  },
});

export const {
  setModelPage,
  setModelLimit,
  updateModelFilters,
  disableModelFilterApplied,
  setModelSortBy,
  setModelSortField,
} = modelSlice.actions;

export default modelSlice.reducer;
